import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextFieldProps,
  useTheme,
} from '@mui/material';
import moment, { Moment } from 'moment';
import { DateTimePicker, LocalizationProvider } from '@mui/lab';
import AdapterMoment from '@mui/lab/AdapterMoment';

import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import {
  ISectionForAdmin,
  TCreateNewCourseSectionAsAdmin,
  createNewCourseSectionAsAdmin,
} from '../../../../../../store/V2/SectionSlice';
import { useAppSelector } from '../../../../../../hooks';
import { TPTextField } from '../../../../../../components/Inputs';
import { TSubsectionCreateNewItemRequestData } from '../../components/SectionItem';

interface AddZoomSectionItemModalProps {
  open: boolean;
  onClose: () => void;
  parentSectionItemData?: TSubsectionCreateNewItemRequestData['parentSection'];
  mode?: 'edit';
  section?: ISectionForAdmin;
}

type TState = {
  title: string;
  dateTime: Moment | null;
  classDuration: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const initialState: TState = {
  title: '',
  dateTime: null,
  classDuration: '',
};

const AddZoomSectionItemModal: React.FC<AddZoomSectionItemModalProps> = ({
  open,
  onClose,
  parentSectionItemData,
  mode,
  section,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState(initialState);

  const courseData = coursesState.adminCourse;
  const isSubmitDisabled =
    !state.title.trim().length ||
    !state.dateTime ||
    !(Number(state.classDuration) > 0);

  useEffect(() => {
    if (mode === 'edit' && section) {
      setState((prevState) => {
        const newState: TState = {
          ...prevState,
          title: section.title,
        };

        if (section.dateTime) {
          newState.dateTime = moment(section.dateTime);
        }

        if (section.duration) {
          newState.classDuration = section.duration.toString();
        }

        return newState;
      });
    }
  }, []);

  const handleDateTimeChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        dateTime: value,
      }));
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (courseData && state.dateTime && state.classDuration) {
      if (mode === 'edit' && section) {
        // TODO
      } else {
        // eslint-disable-next-line no-lonely-if
        if (parentSectionItemData) {
          const apiData: TCreateNewCourseSectionAsAdmin = {
            type: 'ZOOM',
            parent: parentSectionItemData._id,
            sortingKey: parentSectionItemData.nextSortingKey,
            course: courseData._id,
            title: state.title.trim(),
            dateTime: state.dateTime.clone().toISOString(),
            duration: Number(state.classDuration),
          };

          dispatch(createNewCourseSectionAsAdmin(apiData));
        }
      }
    }

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{mode === 'edit' ? 'Edit' : 'Zoom'}</DialogTitle>
      <DialogContent sx={{ pt: `${theme.spacing(2)} !important` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="title"
                size="small"
                label="Title *"
                placeholder="Title"
                value={state.title}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  value={state.dateTime}
                  minDateTime={moment()}
                  onChange={handleDateTimeChange}
                  inputFormat="YYYY-MM-DD hh:mm A"
                  label="Date Time *"
                  InputProps={{ readOnly: true }}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      placeholder="Select Date Time"
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="classDuration"
                type="number"
                size="small"
                label="Class Duration (mins) *"
                placeholder="Class Duration (mins)"
                value={state.classDuration}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Save' : 'Add'}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddZoomSectionItemModal;
